import { useState, type FormEvent, type ReactElement } from 'react';
import { messageForError } from '@proctoring/api-client';
import { jobProfileInputSchema, type JobProfile } from '@proctoring/shared';
import {
  useCreateJobProfile,
  useDeleteJobProfile,
  useJobProfiles,
  useUpdateJobProfile,
} from '../api/queries.js';
import { FullPageMessage } from '../components/FullPageMessage.js';

interface DraftState {
  name: string;
  vscodeUrl: string;
}

const EMPTY: DraftState = { name: '', vscodeUrl: '' };

/**
 * Validates a draft against the shared contract.
 *
 * Reusing `jobProfileInputSchema` rather than hand-rolling checks means the URL rule the
 * API enforces and the one the form shows can never drift apart.
 */
function validate(draft: DraftState): { ok: true } | { ok: false; message: string } {
  const parsed = jobProfileInputSchema.safeParse(draft);
  if (parsed.success) return { ok: true };
  const first = parsed.error.issues[0];
  const field = first?.path[0] === 'vscodeUrl' ? 'VSCode URL' : 'Name';
  return { ok: false, message: `${field}: ${first?.message ?? 'is invalid'}` };
}

function ProfileRow({ profile }: { profile: JobProfile }): ReactElement {
  const update = useUpdateJobProfile();
  const remove = useDeleteJobProfile();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<DraftState>({
    name: profile.name,
    vscodeUrl: profile.vscodeUrl,
  });
  const [error, setError] = useState<string | null>(null);
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  async function onSave(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    const result = validate(draft);
    if (!result.ok) {
      setError(result.message);
      return;
    }
    setError(null);
    try {
      await update.mutateAsync({ id: profile.id, input: draft });
      setEditing(false);
    } catch (cause) {
      setError(messageForError(cause));
    }
  }

  if (editing) {
    return (
      <li className="border-b border-session-border p-5 last:border-b-0">
        <form onSubmit={onSave} className="space-y-3" noValidate>
          {error ? (
            <p role="alert" className="form-error">
              {error}
            </p>
          ) : null}
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <label htmlFor={`name-${profile.id}`} className="field-label">
                Name
              </label>
              <input
                id={`name-${profile.id}`}
                value={draft.name}
                onChange={(event) => setDraft({ ...draft, name: event.target.value })}
                className="field-input"
              />
            </div>
            <div>
              <label htmlFor={`url-${profile.id}`} className="field-label">
                VSCode URL
              </label>
              <input
                id={`url-${profile.id}`}
                value={draft.vscodeUrl}
                onChange={(event) => setDraft({ ...draft, vscodeUrl: event.target.value })}
                className="field-input"
              />
            </div>
          </div>
          <div className="flex gap-2">
            <button type="submit" disabled={update.isPending} className="btn-primary text-xs">
              {update.isPending ? 'Saving…' : 'Save'}
            </button>
            <button
              type="button"
              onClick={() => {
                setEditing(false);
                setDraft({ name: profile.name, vscodeUrl: profile.vscodeUrl });
                setError(null);
              }}
              className="btn-secondary text-xs"
            >
              Cancel
            </button>
          </div>
        </form>
      </li>
    );
  }

  return (
    <li
      data-testid="job-profile-row"
      className="border-b border-session-border px-5 py-4 last:border-b-0"
    >
      <div className="flex items-center justify-between gap-4">
        <div className="min-w-0">
          <p className="truncate text-sm font-medium text-session-text">{profile.name}</p>
          <p className="truncate text-xs text-session-dim">{profile.vscodeUrl}</p>
        </div>
        <div className="flex shrink-0 gap-2">
          <button
            type="button"
            onClick={() => setEditing(true)}
            className="btn-secondary px-3 py-1.5 text-xs"
          >
            Edit
          </button>
          {confirmingDelete ? (
            <>
              <button
                type="button"
                onClick={() => {
                  setError(null);
                  // Deleting a profile an interview still references comes back 409. Swallowing
                  // it left the row in place with the button flipping back to "Delete" and no
                  // explanation, which reads as the button being broken.
                  void remove.mutateAsync(profile.id).catch((cause: unknown) => {
                    setError(messageForError(cause));
                    setConfirmingDelete(false);
                  });
                }}
                disabled={remove.isPending}
                className="btn-danger px-3 py-1.5 text-xs"
              >
                {remove.isPending ? 'Deleting…' : 'Confirm'}
              </button>
              <button
                type="button"
                onClick={() => setConfirmingDelete(false)}
                className="btn-secondary px-3 py-1.5 text-xs"
              >
                Cancel
              </button>
            </>
          ) : (
            <button
              type="button"
              onClick={() => setConfirmingDelete(true)}
              className="btn-danger px-3 py-1.5 text-xs"
            >
              Delete
            </button>
          )}
        </div>
      </div>
      {error ? (
        <p role="alert" className="form-error mt-3">
          {error}
        </p>
      ) : null}
    </li>
  );
}

export function JobProfilesPage(): ReactElement {
  const { data, isPending, error } = useJobProfiles();
  const create = useCreateJobProfile();
  const [draft, setDraft] = useState<DraftState>(EMPTY);
  const [formError, setFormError] = useState<string | null>(null);

  async function onCreate(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    const result = validate(draft);
    if (!result.ok) {
      setFormError(result.message);
      return;
    }
    setFormError(null);
    try {
      await create.mutateAsync(draft);
      setDraft(EMPTY);
    } catch (cause) {
      setFormError(messageForError(cause));
    }
  }

  if (isPending) return <FullPageMessage title="Loading job profiles…" />;
  if (error) {
    return <FullPageMessage title="Could not load job profiles" detail={messageForError(error)} />;
  }

  return (
    <div className="mx-auto w-full max-w-4xl px-6 py-10">
      <h1 className="text-xl font-semibold tracking-tight text-session-text">Job profiles</h1>
      <p className="mt-1 text-sm text-session-muted">
        Each profile pins the pre-provisioned web VSCode environment an interview runs in.
      </p>

      <section className="panel mt-8 p-5" aria-labelledby="new-profile">
        <h2 id="new-profile" className="text-sm font-semibold text-session-text">
          New job profile
        </h2>
        <form onSubmit={onCreate} className="mt-4 space-y-3" noValidate>
          {formError ? (
            <p role="alert" className="form-error">
              {formError}
            </p>
          ) : null}
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <label htmlFor="new-name" className="field-label">
                Name
              </label>
              <input
                id="new-name"
                value={draft.name}
                onChange={(event) => setDraft({ ...draft, name: event.target.value })}
                placeholder="Senior TypeScript Engineer"
                className="field-input"
              />
            </div>
            <div>
              <label htmlFor="new-url" className="field-label">
                VSCode URL
              </label>
              <input
                id="new-url"
                value={draft.vscodeUrl}
                onChange={(event) => setDraft({ ...draft, vscodeUrl: event.target.value })}
                placeholder="https://vscode.example.com/typescript"
                className="field-input"
              />
            </div>
          </div>
          <button type="submit" disabled={create.isPending} className="btn-primary">
            {create.isPending ? 'Creating…' : 'Create job profile'}
          </button>
        </form>
      </section>

      <section className="mt-6" aria-labelledby="all-profiles">
        <h2 id="all-profiles" className="mb-3 text-sm font-semibold text-session-text">
          All profiles
        </h2>
        {data.length === 0 ? (
          <p className="panel px-5 py-6 text-sm text-session-muted">
            No job profiles yet. Create one above before scheduling an interview.
          </p>
        ) : (
          <ul className="panel overflow-hidden">
            {data.map((profile) => (
              <ProfileRow key={profile.id} profile={profile} />
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
